import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  AccountBalance as AuditIcon,
  Security as RiskIcon,
  PersonSearch as NominationIcon,
  AttachMoney as RemunerationIcon,
  Gavel as GovernanceIcon,
  Business as ExecutiveIcon,
  TrendingUp as InvestmentIcon,
  Category as OtherIcon,
  ChevronRightOutlined as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CommitteesTab = ({ committees }) => {
  const navigate = useNavigate();

  const roleColors = {
    CHAIRPERSON: { bg: '#FFEBEE', text: '#D32F2F' },
    SECRETARY: { bg: '#E3F2FD', text: '#1976D2' },
    MEMBER: { bg: '#F5F5F5', text: '#757575' },
    ADVISOR: { bg: '#FFF3E0', text: '#E65100' },
    OBSERVER: { bg: '#F5F5F5', text: '#9E9E9E' },
  };

  const getCommitteeIcon = (committee) => {
    // Try to match by type or extract from name
    const type = committee.type || '';
    const name = committee.name || '';
    
    const iconMap = {
      'Audit': AuditIcon,
      'Risk Management': RiskIcon,
      'Nomination': NominationIcon,
      'Remuneration': RemunerationIcon,
      'Governance': GovernanceIcon,
      'Executive': ExecutiveIcon,
      'Investment': InvestmentIcon,
    };
    
    // Try exact match on type
    if (iconMap[type]) return iconMap[type];
    
    // Try to extract from name
    if (name.toLowerCase().includes('audit')) return AuditIcon;
    if (name.toLowerCase().includes('risk')) return RiskIcon;
    if (name.toLowerCase().includes('nomination')) return NominationIcon;
    if (name.toLowerCase().includes('remuneration')) return RemunerationIcon;
    if (name.toLowerCase().includes('governance')) return GovernanceIcon;
    if (name.toLowerCase().includes('executive')) return ExecutiveIcon;
    if (name.toLowerCase().includes('investment')) return InvestmentIcon;
    
    return OtherIcon;
  };

  const getCommitteeColor = (committee) => {
    // Try to match by type or extract from name
    const type = committee.type || '';
    const name = committee.name || '';
    
    const colorMap = {
      'Audit': '#1976D2',
      'Risk Management': '#D32F2F',
      'Nomination': '#7B1FA2',
      'Remuneration': '#388E3C',
      'Governance': '#F57C00',
      'Executive': '#0288D1',
      'Investment': '#00796B',
    };
    
    // Try exact match on type
    if (colorMap[type]) return colorMap[type];
    
    // Try to extract from name
    if (name.toLowerCase().includes('audit')) return '#1976D2';
    if (name.toLowerCase().includes('risk')) return '#D32F2F';
    if (name.toLowerCase().includes('nomination')) return '#7B1FA2';
    if (name.toLowerCase().includes('remuneration')) return '#388E3C';
    if (name.toLowerCase().includes('governance')) return '#F57C00';
    if (name.toLowerCase().includes('executive')) return '#0288D1';
    if (name.toLowerCase().includes('investment')) return '#00796B';
    
    return '#757575';
  };

  const getCommitteeBgColor = (committee) => {
    // Try to match by type or extract from name
    const type = committee.type || '';
    const name = committee.name || '';
    
    const colorMap = {
      'Audit': '#E3F2FD',
      'Risk Management': '#FFEBEE',
      'Nomination': '#F3E5F5',
      'Remuneration': '#E8F5E9',
      'Governance': '#FFF3E0',
      'Executive': '#E1F5FE',
      'Investment': '#E0F2F1',
    };
    
    // Try exact match on type
    if (colorMap[type]) return colorMap[type];
    
    // Try to extract from name
    if (name.toLowerCase().includes('audit')) return '#E3F2FD';
    if (name.toLowerCase().includes('risk')) return '#FFEBEE';
    if (name.toLowerCase().includes('nomination')) return '#F3E5F5';
    if (name.toLowerCase().includes('remuneration')) return '#E8F5E9';
    if (name.toLowerCase().includes('governance')) return '#FFF3E0';
    if (name.toLowerCase().includes('executive')) return '#E1F5FE';
    if (name.toLowerCase().includes('investment')) return '#E0F2F1';
    
    return '#F5F5F5';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return 'N/A';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0 && remainingMonths > 0) {
      return `${years}y ${remainingMonths}m`;
    } else if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    } else if (remainingMonths > 0) {
      return `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    } else {
      return 'Less than 1 month';
    }
  };

  const isActive = (committee) => {
    if (!committee.termEndDate) return true;
    return new Date(committee.termEndDate) >= new Date();
  };

  const activeCommittees = committees?.filter(isActive) || [];
  const pastCommittees = committees?.filter(c => !isActive(c)) || [];

  const renderCommitteeList = (committeeList, title) => {
    if (committeeList.length === 0) return null;

    return (
      <Box sx={{ mb: activeCommittees.length > 0 && pastCommittees.length > 0 ? 4 : 0 }}>
        {title && (
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <List sx={{ p: 0 }}>
          {committeeList.map((committee, index) => {
            const Icon = getCommitteeIcon(committee);
            const active = isActive(committee);

            return (
              <React.Fragment key={committee.id}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                  onClick={() => navigate(`/committees/${committee.id}`)}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: getCommitteeBgColor(committee),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mr: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 24, color: getCommitteeColor(committee) }} />
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {committee.name}
                        </Typography>
                        <Chip
                          label={committee.memberRole || 'MEMBER'}
                          sx={{
                            bgcolor: (roleColors[committee.memberRole] || roleColors.MEMBER).bg,
                            color: (roleColors[committee.memberRole] || roleColors.MEMBER).text,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                          size="small"
                        />
                        {!active && (
                          <Chip
                            label="PAST MEMBERSHIP"
                            sx={{
                              bgcolor: '#FFF3E0',
                              color: '#E65100',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                            size="small"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          {committee.appointmentDate 
                            ? `Started: ${formatDate(committee.appointmentDate)}` 
                            : 'Start date: N/A'}
                        </Typography>
                        {committee.termEndDate && (
                          <Typography variant="body2" color="textSecondary">
                            • {active ? 'Ends' : 'Ended'}: {formatDate(committee.termEndDate)}
                          </Typography>
                        )}
                        {committee.appointmentDate && (
                          <Typography variant="body2" color="textSecondary">
                            • {calculateDuration(committee.appointmentDate, committee.termEndDate)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <IconButton size="small">
                    <ChevronRightIcon />
                  </IconButton>
                </ListItem>
                {index < committeeList.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    );
  };

  if (!committees || committees.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Committee Memberships
        </Typography>
        <Box textAlign="center" py={4}>
          <OtherIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Not assigned to any committees
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Committee Memberships ({committees.length})
        </Typography>
      </Box>

      {activeCommittees.length > 0 && (
        <Box sx={{ mb: pastCommittees.length > 0 ? 4 : 0 }}>
          {renderCommitteeList(activeCommittees, null)}
        </Box>
      )}

      {activeCommittees.length > 0 && pastCommittees.length > 0 && (
        <Divider sx={{ my: 4 }}>
          <Chip 
            label="Past Memberships" 
            size="small"
            sx={{
              bgcolor: '#FFF3E0',
              color: '#E65100',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          />
        </Divider>
      )}

      {pastCommittees.length > 0 && (
        <Box>
          {renderCommitteeList(pastCommittees, null)}
        </Box>
      )}
    </Paper>
  );
};

export default CommitteesTab;
